import { useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import api from "../../../utils/api";
import { exportDemoReport, loadReportDemos } from "./reportUtils";

export default function DemoReport({ disabled }) {
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(1, "month").add(1, "day").startOf("day"));
  const [toDate, setToDate] = useState(() => dayjs().startOf("day"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const exporting = useRef(false);
  const validRange = Boolean(
    fromDate?.isValid() &&
      toDate?.isValid() &&
      !fromDate.isAfter(toDate, "day") &&
      !toDate.isAfter(dayjs(), "day")
  );

  const exportPdf = async () => {
    if (exporting.current) return;
    if (!validRange) {
      setError("Select valid From and To dates. From must be on or before To, and dates cannot be in the future.");
      return;
    }
    exporting.current = true;
    setBusy(true);
    setError("");
    try {
      const range = {
        from: fromDate.startOf("day").toISOString(),
        to: toDate.endOf("day").toISOString(),
      };
      const rows = await loadReportDemos(api, range);
      await exportDemoReport({ rows, range });
    } catch (e) {
      setError(e?.message || "PDF could not be exported. Please try again.");
    } finally {
      exporting.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Demo report" disableHoverListener={open}>
        <span>
          <IconButton
            aria-label="Open demo report"
            size="small"
            disabled={disabled}
            onClick={() => {
              setError("");
              setOpen(true);
            }}
            sx={{
              width: 34,
              height: 34,
              border: "1px solid #2D4F91",
              color: "#2D4F91",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "rgba(45, 79, 145, 0.08)" },
            }}
          >
            <AssessmentOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, color: "#284889", fontWeight: 700 }}>
          Demo report
          <IconButton aria-label="Close demo report" disabled={busy} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* <Box sx={{ color: "#657A99", fontSize: 13, mb: 2 }}>
            Same layout as the finance report. It includes parent and tutor details, and counts completed demos whose class is running (the rupee mark).
          </Box> */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" pb={1}>
            <CustomDatePicker
              label="From"
              value={fromDate}
              onChange={(value) => {
                setFromDate(value);
                setError("");
              }}
              width={160}
              maxDate={dayjs()}
              disabled={busy}
            />
            <CustomDatePicker
              label="To"
              value={toDate}
              onChange={(value) => {
                setToDate(value);
                setError("");
              }}
              width={160}
              minDate={fromDate?.isValid() ? fromDate : undefined}
              maxDate={dayjs()}
              disabled={busy}
            />
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={busy || !validRange}
              onClick={exportPdf}
              sx={{
                backgroundColor: "#2D4F91",
                color: "#FFFFFF",
                fontWeight: 600,
                px: 3,
                height: 50,
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 10px rgba(45, 79, 145, 0.25)",
                "&:hover": {
                  backgroundColor: "#233F78",
                  boxShadow: "0 6px 14px rgba(45, 79, 145, 0.35)",
                },
              }}
            >
              {busy ? "Exporting..." : "Export PDF"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

DemoReport.propTypes = {
  disabled: PropTypes.bool,
};
